/**
 * English localization for react-pivot. Acts as the default dictionary
 * merged on top of any caller override via PivotProvider. Keys mirror
 * the Italian dictionary (it.js) so the two can be swapped freely.
 */

const en = {
  fieldsList: {
    hierarchyBox: 'Drag Dimensions',
    filterBox: 'Drop & Order Report Filters',
    rowBox: 'Drop & Order Rows',
    columnBox: 'Drop & Order Columns',
    measureBox: 'Drop & Order Values',
    values: 'Values',
    expandAll: 'Expand All',
    collapseAll: 'Collapse All',
    allFields: 'All fields',
    rows: 'Rows',
    columns: 'Columns',
    filters: 'Filters',
    dropField: 'Drop field here',
    title: 'Fields',
    subtitle: 'Drag and drop fields to reorder',
    addCalculated: 'Add calculated value',
    measuresAxisTooltip:
      'Special aggregation field — drag to Rows or Columns to choose the values axis',
    calculatedFieldTooltip: 'Calculated field',
    showTotals: 'Show totals',
    perRow: 'Per row',
    perColumn: 'Per column',
    dateFormat: 'Date format',
    dateFormatHelp:
      'Tokens: yyyy yy MMMM MMM MM M dd d EEEE EEE HH H mm m ss s',
    dateFormats: {
      localeDate: 'Browser locale (date)',
      localeDateTime: 'Browser locale (date & time)',
      iso: 'ISO (yyyy-MM-dd HH:mm:ss)',
      isoDate: 'ISO date (yyyy-MM-dd)',
      custom: 'Custom pattern…',
    },
    subpartFormats: {
      month: {
        'name-full': 'Full name (January)',
        'name-short': 'Short name (Jan)',
        number: 'Number (1)',
        'number-padded': 'Padded number (01)',
      },
      quarter: {
        long: 'Long (Quarter 1)',
        short: 'Short (Q1)',
        number: 'Number (1)',
      },
      weekday: {
        'name-full': 'Full name (Monday)',
        'name-short': 'Short name (Mon)',
      },
      week: {
        number: 'Number (1)',
        long: 'Long (Week 1)',
      },
    },
  },
  grid: {
    total: 'Total',
    grandTotal: 'Grand Total',
    blankMember: '(blank)',
    loading: 'Processing…',
    empty: 'No data to display',
    expandCollapseAll: 'Expand/Collapse all',
    expandCollapseChildren: 'Expand / Collapse level below',
    grandTotalMeasureCaptionTemplate: 'Total of {field} ({agg})',
    measureCaptionTemplate: '{field} ({agg})',
    hiddenMeasures: 'Other measures',
    formulaError: 'Formula error',
    sortAsc: 'Ascending',
    sortDesc: 'Descending',
    sortByMeasure: 'Sort by measure',
    removeSort: 'Remove sort',
  },
  aggregations: {
    sum: 'Sum',
    count: 'Count',
    distinctcount: 'Distinct count',
    average: 'Average',
    min: 'Minimum',
    max: 'Maximum',
    ratioTotal: 'Ratio to total',
    currentRatio: 'Incidence',
  },
  buttons: {
    ok: 'OK',
    apply: 'Apply',
    cancel: 'Cancel',
    save: 'Save',
    reset: 'Reset',
    close: 'Close',
    edit: 'Edit',
    delete: 'Delete',
    saveChanges: 'Save changes',
    create: 'Create field',
    removeFilter: 'Remove filter',
  },
  toolbar: {
    fields: 'Fields',
    format: 'Format',
    export: 'Export',
    exportExcel: 'Export to Excel',
    fullscreen: 'Fullscreen',
    exitFullscreen: 'Exit Fullscreen',
  },
  filterBar: {
    title: 'Filters',
    all: 'All',
    values: 'values',
  },
  filterEditor: {
    modeSingle: 'Single value',
    modeSingleDate: 'Single date',
    modeMulti: 'Multiple values',
    modeRange: 'Range',
    rangeFrom: 'From',
    rangeTo: 'To',
    value: 'Value',
    date: 'Date',
    search: 'Search…',
    selectAll: 'Select all',
    noValues: 'No values.',
  },
  dimensionFilter: {
    subtitle: 'Select the values to include in the pivot.',
    search: 'Search…',
    selectAll: 'Select all',
    noValues: 'No values.',
    sortBy: 'Sort by',
    columnSortNotice:
      'A column sort is active and takes precedence over this sort.',
    rowSortNotice:
      'A row sort is active and takes precedence over this sort.',
  },
  drillThrough: {
    title: 'Detail data',
    record: 'record',
    recordsFound: 'records found',
    noRecords: 'No records.',
  },
  calculatedField: {
    groupArithmetic: 'Arithmetic',
    groupComparison: 'Comparison / Logic',
    groupFunctions: 'Functions',
    groupAggregations: 'Aggregations & Running',
    groupConstants: 'Constants',
    tooltipIF: 'IF(condition, value_if_true, value_if_false)',
    tooltipABS: 'Absolute value',
    tooltipMIN: 'Minimum of two or more numbers',
    tooltipMAX: 'Maximum of two or more numbers',
    tooltipSUM: 'Sum of a field',
    tooltipCOUNT: 'Record count',
    tooltipAVG: 'Average of a field',
    tooltipMINField: 'Minimum of the field',
    tooltipMAXField: 'Maximum of the field',
    tooltipRunningSum:
      'Running (cumulative) sum of the field over visible rows',
    invalidFormula: 'Invalid formula',
    errorNameRequired: 'Field name is required.',
    errorFormulaRequired: 'Formula is required.',
    editTitle: 'Edit calculated field',
    createTitle: 'Add calculated field',
    nameLabel: 'Field name',
    formulaLabel: 'Formula',
    formulaPlaceholder: 'Example: sum("revenue") / count("orders") * 100',
    availableFields:
      'Available numeric fields — click to insert into the formula',
  },
  formatDialog: {
    subtitle: 'Customize cell appearance and conditional formatting rules.',
    font: 'Font',
    fontDefault: 'Default (theme)',
    fontSize: 'Size',
    textColorLabel: 'Text color',
    bgColorLabel: 'Background color',
    numberFormat: 'NUMBER FORMAT',
    decimals: 'Decimals',
    decimalsHint: 'empty = auto',
    currencySymbol: 'Currency symbol',
    currencyPlaceholder: '$',
    thousandsSeparator: 'Thousands separator',
    thousandSeparator: 'Thousand Separator',
    decimalSeparator: 'Decimal Separator',
    numberOfDecimals: 'Number of decimals',
    currencyOther: 'Symbol',
    currencyAlignment: 'Currency alignment',
    nullValue: 'Null Value',
    percentage: 'Format as percentage',
    system: 'System',
    none: 'None',
    other: 'Other',
    default: 'Default',
    left: 'Left',
    right: 'Right',
    totalsPosition: 'TOTALS POSITION',
    totalsPositionDesc:
      'Defines whether subtotals and grand totals are shown before or after the data they aggregate.',
    totalsPerRow: 'Totals per row',
    totalsPerColumn: 'Totals per column',
    beforeData: 'Before data',
    afterData: 'After data',
    noTotals: 'None',
    readability: 'READABILITY',
    alternateRows: 'Alternating rows (zebra stripes for easier reading)',
    density: 'DENSITY',
    densityStandard: 'Standard',
    densityCompact: 'Compact',
    densityComfortable: 'Comfortable',
    operators: {
      gt: 'Greater than (>)',
      gte: 'Greater or equal (≥)',
      lt: 'Less than (<)',
      lte: 'Less or equal (≤)',
      eq: 'Equal (=)',
      neq: 'Not equal (≠)',
      between: 'Between',
    },
    rule: 'Rule',
    rulesDesc: 'Drag to reorder Rules.',
    conditionalModeLabel: 'Evaluation mode',
    conditionalModeInherit: 'Inherit',
    conditionalModeFirst: 'Stop at the first rule matched',
    conditionalModeAll: 'Evaluate all the rules',
    conditionalModeFirstDesc:
      'Rules are evaluated from top to bottom; the first matching rule is applied.',
    conditionalModeAllDesc:
      'Rules are evaluated from top to bottom; every matching rule is applied — later rules override earlier ones for the properties they set.',
    allMeasures: 'All measures',
    ruleValue: 'Value',
    ruleValueAnd: 'and',
    ruleTextColor: 'Text',
    ruleBgColor: 'Background',
    preview: 'preview',
    addRule: 'Add rule',
    noRules: 'No rules defined.',
    tabs: {
      headers: 'Headers',
      grandTotals: 'Grand totals',
      dimensions: 'Dimensions',
      layout: 'Layout',
      values: 'Values',
      conditional: 'Conditional',
    },
    valuesTarget: 'Apply to',
    valuesDefault: 'Default (all measures)',
    clearOverride: 'Use default',
    clauseMany: 'clauses',
    clauseOne: 'clause',
    noClauses: 'no clauses',
    ruleValueAnd: 'and',
    ruleValueConstant: 'Constant',
    ruleValueMeasure: 'Measure',
    selectMeasure: 'Select measure',
    ruleValueUpperBound: 'Upper bound',
    conditionalModeLabel: 'Evaluation mode',
    preview: 'preview',
    expression: 'Expression',
    addClause: 'Add Clause',
    expressionJoin: 'Combine clauses with',
    editExpression: 'Edit expression',
    expressionSubtitle:
      'Combine clauses on dimensions and measures. The expression is evaluated per cell; the rule fires when it returns true.',
    value: 'Value',
    exprAnd: 'AND',
    exprOr: 'OR',
    exprStartsWith: 'Starts with',
    exprEndsWith: 'Ends with',
    exprContains: 'Contains',
    exprEquals: 'Equals',
    exprNot: 'NOT',
  },
  dates: {
    monthNames: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    weekdayNames: [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ],
    hierarchyParts: {
      year: 'Year',
      quarter: 'Quarter',
      month: 'Month',
      week: 'Week',
      day: 'Day',
      weekday: 'Weekday',
      hour: 'Hour',
      minute: 'Minute',
    },
    quarterLabel: 'Quarter',
    quarterShortPrefix: 'Q',
    weekLabel: 'Week',
  },
};

export default en;

export const mergeLocalization = (base, override) => {
  if (!override) return base;
  const out = { ...base };
  Object.keys(override).forEach((section) => {
    const baseSection = base[section];
    const overrideSection = override[section];
    if (
      baseSection &&
      overrideSection &&
      typeof baseSection === 'object' &&
      typeof overrideSection === 'object' &&
      !Array.isArray(baseSection) &&
      !Array.isArray(overrideSection)
    ) {
      out[section] = { ...baseSection, ...overrideSection };
    } else {
      out[section] = overrideSection;
    }
  });
  return out;
};
